import { useState, useEffect } from 'react';
import { useQuiz } from '../context/QuizContext';
import { fetchItensTenda } from '../services/quizFirebaseService';
import { Coins, Heart, Loader2, ArrowLeft, ShoppingCart } from 'lucide-react';
import type { ItemTendaKey, Inventario } from '../types/quiz.types';
import { useGameAudio } from '../hooks/useGameAudio';

type ItemTenda = {
  id: string;
  slug?: string;
  nome: string;
  custo?: number;
  preco?: number;
  fase_minima?: number;
  descricao_curta?: string;
  descricao?: string;
};

const ITEM_TENDA_KEYS: ItemTendaKey[] = [
  'axe_dos_deuses',
  'ubuntu',
  'conselho_grios',
  'caminho_mandela',
  'escudo_palmares',
  'vida_1',
  'vidas_4',
  'vidas_5',
];

function resolveItemKey(item: ItemTenda): ItemTendaKey | null {
  const raw = item.slug ?? item.id;
  const key = raw === 'vidas_5' ? 'vidas_4' : raw;
  return ITEM_TENDA_KEYS.includes(key as ItemTendaKey) ? (key as ItemTendaKey) : null;
}

function isVidaKey(key: ItemTendaKey): boolean {
  return key === 'vida_1' || key === 'vidas_4' || key === 'vidas_5';
}

export function TendaScreen() {
  const { state, dispatch } = useQuiz();
  const { playHover, playClick, playBack, playBlocked, playSuccess, playError } = useGameAudio();
  const [itensTenda, setItensTenda] = useState<ItemTenda[]>([]);
  const [loading, setLoading] = useState(true);

  const veioDaPartida = state.statusAntesDaTenda !== null;
  const comprasBloqueadasFase50 = state.faseAtual === 50 && veioDaPartida;

  useEffect(() => {
    let isMounted = true;
    fetchItensTenda()
      .then(data => {
        if (isMounted) {
          setItensTenda(data);
          setLoading(false);
        }
      })
      .catch((e) => {
        console.error(e);
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  const handleVoltar = () => {
    playBack();
    if (veioDaPartida) {
      dispatch({ type: 'RETURN_FROM_TENDA' });
    } else {
      dispatch({ type: 'NAVIGATE', payload: 'MENU' });
    }
  };

  const showBarraCheia = () => {
    playBlocked();
    dispatch({
      type: 'SHOW_FEEDBACK',
      payload: { message: 'Sua barra de Búzios Vitais já está cheia (5/5).', type: 'info' },
    });
  };

  const showFaseMinima = (faseMinima: number) => {
    playBlocked();
    dispatch({
      type: 'SHOW_FEEDBACK',
      payload: { message: `Disponível a partir da Fase ${faseMinima}.`, type: 'warning' },
    });
  };

  const showComprasBloqueadas = () => {
    playBlocked();
    dispatch({
      type: 'SHOW_FEEDBACK',
      payload: { message: 'Na Fase 50, compras estão bloqueadas durante a partida.', type: 'warning' },
    });
  };

  const getQuantidade = (key: ItemTendaKey) => {
    if (isVidaKey(key)) return null;
    return state.inventario[key as keyof Inventario] ?? 0;
  };

  const handleComprar = (item: ItemTenda, key: ItemTendaKey, preco: number) => {
    if (comprasBloqueadasFase50) {
      showComprasBloqueadas();
      return;
    }
    if (item.fase_minima && state.faseAtual < item.fase_minima) {
      showFaseMinima(item.fase_minima);
      return;
    }
    if (state.moedas < preco) {
      playBlocked();
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: { message: 'Axé insuficiente para esta compra.', type: 'warning' },
      });
      return;
    }
    if (isVidaKey(key)) {
      if (state.vidas >= state.maxVidas) {
        showBarraCheia();
        return;
      }
    } else if (!(key in state.inventario)) {
      playError();
      return;
    }
    playClick();
    playSuccess();
    dispatch({ type: 'COMPRAR_ITEM', payload: { key, custo: preco, faseMinima: item.fase_minima } });
  };

  const handleComprarVidas = (quantidade: number, custo: number) => {
    if (comprasBloqueadasFase50) {
      showComprasBloqueadas();
      return;
    }
    if (state.moedas < custo) {
      playBlocked();
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: { message: 'Axé insuficiente para esta compra.', type: 'warning' },
      });
      return;
    }
    if (state.vidas >= state.maxVidas) {
      showBarraCheia();
      return;
    }
    playClick();
    playSuccess();
    dispatch({ type: 'COMPRAR_VIDAS', payload: { quantidade, custo } });
  };

  const handleClickVidaDesabilitado = (_quantidade: number) => {
    playClick();
    if (comprasBloqueadasFase50) {
      showComprasBloqueadas();
      return;
    }
    if (state.vidas >= state.maxVidas) {
      showBarraCheia();
      return;
    }
  };

  return (
    <div className="h-full max-h-[100dvh] flex flex-col p-3 sm:p-4 w-full max-w-3xl mx-auto animate-in fade-in duration-300 relative z-10 overflow-hidden">

      {/* Header Compacto Fixo */}
      <div className="glass-panel p-2.5 px-3 sm:px-4 rounded-xl mb-3 border-amber-500/30 flex items-center justify-between gap-3 bg-slate-900/80 shadow-neon-amber shrink-0">
        <div className="flex items-center gap-3">
          <button
            onMouseEnter={playHover}
            onClick={handleVoltar}
            className="p-2 rounded-lg bg-slate-800 text-amber-400 hover:bg-amber-500 hover:text-slate-900 transition border border-slate-700 hover:border-amber-400 shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-cyber font-black tracking-wider text-sm sm:text-base uppercase text-amber-500 flex items-center gap-2 drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]">
              <ShoppingCart size={18} />
              Tenda de Saberes
            </h1>
            {veioDaPartida && (
              <p className="text-[10px] text-sky-400/80 font-cyber tracking-wider uppercase">
                {comprasBloqueadasFase50 ? 'Fase 50: Bloqueado' : 'Partida pausada'}
              </p>
            )}
          </div>
        </div>
        <div className="px-3 py-1 bg-slate-950 border border-amber-500/50 rounded-lg flex items-center gap-1.5 shrink-0">
          <Coins size={16} className="text-amber-400 drop-shadow-[0_0_5px_currentColor]" />
          <span className="font-cyber font-bold text-xs sm:text-sm text-amber-100 tracking-wider">{state.moedas}</span>
          <span className="text-[10px] text-amber-500/70 font-cyber font-bold uppercase tracking-widest hidden sm:inline">Axé</span>
        </div>
      </div>

      {/* Opção B: Conteúdo com scroll interno contido e fading de borda suave */}
      <div className="flex-1 overflow-y-auto custom-scrollbar scroll-edge-fade pr-1 pb-4 flex flex-col gap-3 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-8 glass-panel rounded-xl border-amber-500/20 my-auto">
            <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-3" />
            <p className="font-cyber uppercase tracking-widest text-amber-500/50 text-xs animate-pulse">Invocando Mercadores...</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {itensTenda.filter((item) => {
              const key = resolveItemKey(item);
              return key !== null && !isVidaKey(key);
            }).map((item) => {
              const itemKey = resolveItemKey(item);
              const qty = itemKey ? getQuantidade(itemKey) : null;
              const preco = item.custo ?? item.preco ?? 0;
              const bloqueadoFase = Boolean(item.fase_minima && state.faseAtual < item.fase_minima);
              const podeComprar = Boolean(itemKey) && state.moedas >= preco && !bloqueadoFase && !comprasBloqueadasFase50;

              return (
                <div
                  key={item.id}
                  className="glass-panel rounded-xl border border-slate-700/80 hover:border-amber-500/50 p-3.5 flex flex-col justify-between gap-3 group transition-all"
                >
                  <div>
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-cyber font-bold text-sm sm:text-base text-white group-hover:text-amber-400 transition-colors drop-shadow-md">{item.nome}</h3>
                      <div className="bg-amber-900/40 border border-amber-500/30 text-amber-400 px-2 py-0.5 rounded flex items-center gap-1 text-xs shrink-0">
                        <Coins size={12} />
                        <span className="font-cyber font-bold">{preco}</span>
                      </div>
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed">{item.descricao_curta || item.descricao}</p>
                    {item.fase_minima && item.fase_minima > 1 && (
                      <p className="text-slate-500 text-[10px] mt-1 font-cyber uppercase tracking-widest">Fase mínima: {item.fase_minima}</p>
                    )}
                    {qty !== null && (
                      <p className="text-sky-400 text-[10px] font-cyber font-bold tracking-wider uppercase mt-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse"></span>
                        Inventário: {qty}
                      </p>
                    )}
                  </div>

                  <button
                    onMouseEnter={playHover}
                    onClick={() => itemKey && (podeComprar ? handleComprar(item, itemKey, preco) : bloqueadoFase ? showFaseMinima(item.fase_minima!) : comprasBloqueadasFase50 ? showComprasBloqueadas() : playError())}
                    disabled={!itemKey || (!podeComprar && state.moedas >= preco && !bloqueadoFase && !comprasBloqueadasFase50)}
                    className="btn-cyber w-full py-2 rounded-lg flex items-center justify-center gap-1.5 text-xs font-cyber font-bold tracking-wider
                      disabled:opacity-30 disabled:grayscale transition-all
                      border border-slate-600 text-slate-300 bg-slate-800
                      hover:text-amber-950 hover:bg-amber-400 hover:border-amber-400 hover:shadow-neon-amber"
                  >
                    <ShoppingCart size={14} />
                    ADQUIRIR
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="p-3.5 glass-panel rounded-xl border-l-4 border-l-red-500 bg-gradient-to-r from-red-950/30 to-transparent">
          <h3 className="font-cyber font-bold text-sm sm:text-base text-white flex items-center gap-1.5 mb-1">
            <Heart size={16} className="text-red-500" fill="currentColor" />
            Restaurar Búzios Vitais
          </h3>
          <p className="text-slate-400 text-xs mb-3">1 Búzio = 150 Axé | 4 Búzios = 600 Axé</p>
          <div className="flex gap-2">
            <button
              onMouseEnter={playHover}
              onClick={() =>
                state.moedas >= 150 && state.vidas < state.maxVidas && !comprasBloqueadasFase50
                  ? handleComprarVidas(1, 150)
                  : handleClickVidaDesabilitado(1)
              }
              disabled={comprasBloqueadasFase50 || state.moedas < 150}
              className="btn-cyber flex-1 py-2 rounded-lg flex items-center justify-center gap-1 border border-red-500/50 text-red-100 bg-red-900/20 hover:bg-red-500 hover:text-white hover:border-red-400 disabled:opacity-30 transition-all text-xs font-cyber font-bold"
            >
              <Heart size={13} fill="currentColor" /> 1 Búzio (150 Axé)
            </button>
            <button
              onMouseEnter={playHover}
              onClick={() =>
                state.moedas >= 600 && state.vidas < state.maxVidas && !comprasBloqueadasFase50
                  ? handleComprarVidas(4, 600)
                  : handleClickVidaDesabilitado(4)
              }
              disabled={comprasBloqueadasFase50 || state.moedas < 600}
              className="btn-cyber flex-1 py-2 rounded-lg flex items-center justify-center gap-1 border border-red-500/50 text-red-100 bg-red-900/20 hover:bg-red-500 hover:text-white hover:border-red-400 disabled:opacity-30 transition-all text-xs font-cyber font-bold"
            >
              <Heart size={13} fill="currentColor" /> 4 Búzios (600 Axé)
            </button>
          </div>
        </div>
      </div>

      {/* Saída fixa na zona do polegar (UX3): fora da área rolável. Mid-run é
          o único caminho de volta à partida sem alcançar a seta no topo. */}
      <button
        onMouseEnter={playHover}
        onClick={handleVoltar}
        className="btn-cyber w-full py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 font-cyber font-bold tracking-wider hover:text-white hover:bg-slate-700 transition uppercase text-xs shrink-0 mt-2"
      >
        {veioDaPartida ? 'Voltar à Partida' : 'Voltar ao Menu Principal'}
      </button>

    </div>
  );
}
